import { DADATA_TOKEN } from '../../services/environment'

const SUGGEST_PARTY_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/party'
const FIND_PARTY_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/findById/party'
const SUGGEST_BANK_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/suggest/bank'
const FIND_BANK_URL = 'https://suggestions.dadata.ru/suggestions/api/4_1/rs/findById/bank'

export type DaDataPartySuggestion = {
  value: string
  unrestricted_value?: string
  data: {
    inn?: string | null
    kpp?: string | null
    ogrn?: string | null
    type?: string | null
    name?: {
      full_with_opf?: string | null
      short_with_opf?: string | null
      full?: string | null
      short?: string | null
    } | null
    fio?: {
      surname?: string | null
      name?: string | null
      patronymic?: string | null
    } | null
    address?: {
      value?: string | null
      unrestricted_value?: string | null
    } | null
    management?: {
      name?: string | null
      post?: string | null
    } | null
  }
}

export type DaDataBankSuggestion = {
  value: string
  unrestricted_value?: string
  data: {
    bic?: string | null
    swift?: string | null
    correspondent_account?: string | null
    name?: {
      payment?: string | null
      full?: string | null
      short?: string | null
    } | null
  }
}

export type PollRequisitePatch = Partial<{
  'requisite.name': string
  'requisite.inn': string
  'requisite.kpp': string
  'requisite.ogrn': string
  'requisite.address': string
  'requisite.director': string
  'requisite.bik': string
  'requisite.bank_name': string
  'requisite.invoice_corr': string
}>

async function postDaData<T>(
  url: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<T[]> {
  const response = await fetch(url, {
    method: 'POST',
    mode: 'cors',
    signal,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Token ${DADATA_TOKEN}`,
    },
    body: JSON.stringify(body),
  })
  if (!response.ok) {
    throw new Error(`DaData HTTP ${response.status}`)
  }
  const json = await response.json()
  return Array.isArray(json?.suggestions) ? json.suggestions : []
}

export async function suggestParty(
  query: string,
  count = 8,
  signal?: AbortSignal,
): Promise<DaDataPartySuggestion[]> {
  const q = String(query || '').trim()
  if (q.length < 3) return []
  return postDaData<DaDataPartySuggestion>(SUGGEST_PARTY_URL, { query: q, count }, signal)
}

export async function findPartyByInn(
  inn: string,
  signal?: AbortSignal,
): Promise<DaDataPartySuggestion | null> {
  const q = String(inn || '').replace(/\D/g, '')
  if (q.length !== 10 && q.length !== 12) return null
  const list = await postDaData<DaDataPartySuggestion>(
    FIND_PARTY_URL,
    { query: q, count: 1 },
    signal,
  )
  return list[0] || null
}

export async function suggestBank(
  query: string,
  count = 8,
  signal?: AbortSignal,
): Promise<DaDataBankSuggestion[]> {
  const q = String(query || '').trim()
  if (q.length < 2) return []
  return postDaData<DaDataBankSuggestion>(SUGGEST_BANK_URL, { query: q, count }, signal)
}

export async function findBankByBik(
  bik: string,
  signal?: AbortSignal,
): Promise<DaDataBankSuggestion | null> {
  const q = String(bik || '').replace(/\D/g, '')
  if (q.length !== 9) return null
  const list = await postDaData<DaDataBankSuggestion>(FIND_BANK_URL, { query: q, count: 1 }, signal)
  return list[0] || null
}

function fioToString(fio: DaDataPartySuggestion['data']['fio']): string {
  if (!fio) return ''
  return [fio.surname, fio.name, fio.patronymic].filter(Boolean).join(' ').trim()
}

/** Маппинг ответа DaData → system_key реквизитов опроса. */
export function mapPartyToRequisites(party: DaDataPartySuggestion): PollRequisitePatch {
  const data = party?.data || ({} as DaDataPartySuggestion['data'])
  const name =
    data.name?.full_with_opf ||
    data.name?.short_with_opf ||
    data.name?.full ||
    data.name?.short ||
    party.value ||
    ''
  const address = data.address?.unrestricted_value || data.address?.value || ''
  const director = data.management?.name || fioToString(data.fio) || ''
  const patch: PollRequisitePatch = {}
  if (data.inn) patch['requisite.inn'] = String(data.inn)
  if (name) patch['requisite.name'] = name
  if (address) patch['requisite.address'] = address
  if (director) patch['requisite.director'] = director
  if (data.kpp) patch['requisite.kpp'] = String(data.kpp)
  if (data.ogrn) patch['requisite.ogrn'] = String(data.ogrn)
  return patch
}

/** Маппинг банка DaData → system_key реквизитов опроса. */
export function mapBankToRequisites(bank: DaDataBankSuggestion): PollRequisitePatch {
  const data = bank?.data || ({} as DaDataBankSuggestion['data'])
  const bankName = data.name?.payment || data.name?.full || data.name?.short || bank.value || ''
  const patch: PollRequisitePatch = {}
  if (data.bic) patch['requisite.bik'] = String(data.bic)
  if (bankName) patch['requisite.bank_name'] = bankName
  if (data.correspondent_account) {
    patch['requisite.invoice_corr'] = String(data.correspondent_account)
  }
  return patch
}
